/**
 * Paddle 预加载服务
 * 优化 Paddle SDK 的加载速度，支持立即预加载
 */

import { initializePaddle } from '@paddle/paddle-js';
import { getPaddleInitConfig, PADDLE_OPTIMIZATION_CONFIG } from '../config/paddle';
import { log } from '../utils/logger';
import { startPaddleMonitoring, endPaddleMonitoring, endPaddleMonitoringWithError } from '../utils/paddlePerformanceMonitor';

interface PaddleInstance {
  Checkout: {
    open: (config: any) => Promise<void>;
    close: () => void;
  };
}

class PaddlePreloader {
  private paddleInstance: PaddleInstance | null = null;
  private initPromise: Promise<PaddleInstance> | null = null;
  private isInitialized = false;
  private initStartTime = 0;
  private preloadStarted = false;

  /**
   * 预加载 Paddle SDK（优化版本）
   * @param options.priority - 加载优先级：'immediate' 立即加载，'idle' 空闲时加载
   */
  async preload(options?: { priority?: 'immediate' | 'idle' }): Promise<void> {
    // 如果已经在加载或已加载，返回现有的 Promise
    if (this.initPromise) {
      return this.initPromise.then(() => {});
    }

    // 标记预加载已开始
    this.preloadStarted = true;
    this.initStartTime = Date.now();
    
    const priority = options?.priority || 'immediate';
    startPaddleMonitoring('preload');
    log.debug(`🚀 开始预加载 Paddle SDK (优先级: ${priority})...`);

    this.initPromise = this.initializePaddleSDK();
    
    try {
      await this.initPromise;
      const loadTime = Date.now() - this.initStartTime;
      endPaddleMonitoring('preload');
      log.info(`✅ Paddle SDK 预加载完成，耗时: ${loadTime}ms`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      endPaddleMonitoringWithError('preload', errorMessage);
      log.error('❌ Paddle SDK 预加载失败:', error);
      this.initPromise = null; // 允许重试
      throw error;
    }
  }

  /**
   * 初始化 Paddle SDK
   */
  private async initializePaddleSDK(): Promise<PaddleInstance> {
    try {
      // 使用优化配置初始化
      const config = getPaddleInitConfig();
      
      // 添加超时保护
      const initPromise = initializePaddle(config);
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Paddle 初始化超时')), PADDLE_OPTIMIZATION_CONFIG.initTimeout);
      });

      const paddle = await Promise.race([initPromise, timeoutPromise]) as PaddleInstance;

      this.paddleInstance = paddle;
      this.isInitialized = true;
      return paddle;
    } catch (error) {
      this.isInitialized = false;
      throw new Error(`Paddle SDK 初始化失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  /**
   * 获取 Paddle 实例
   */
  async getInstance(): Promise<PaddleInstance> {
    if (this.paddleInstance && this.isInitialized) {
      return this.paddleInstance;
    }

    if (this.initPromise) {
      return await this.initPromise;
    }

    // 如果没有预加载，立即初始化
    log.warn('⚠️ Paddle SDK 未预加载，正在立即初始化...');
    await this.preload({ priority: 'immediate' });
    return this.paddleInstance!;
  }

  /**
   * 检查是否已准备就绪
   */
  isReady(): boolean {
    return this.isInitialized && this.paddleInstance !== null;
  }

  /**
   * 获取初始化状态
   */
  getStatus(): 'not-started' | 'loading' | 'ready' | 'error' {
    if (this.isInitialized && this.paddleInstance) {
      return 'ready';
    }
    if (this.initPromise) {
      return 'loading';
    }
    return 'not-started';
  }

  /**
   * 检查是否已开始预加载
   */
  hasPreloadStarted(): boolean {
    return this.preloadStarted;
  }

  /**
   * 重置状态（用于错误恢复）
   */
  reset(): void {
    this.paddleInstance = null;
    this.initPromise = null;
    this.isInitialized = false;
    this.initStartTime = 0;
    this.preloadStarted = false;
  }
}

// 创建全局实例
export const paddlePreloader = new PaddlePreloader();

// 导出便捷方法
export const preloadPaddle = (options?: { priority?: 'immediate' | 'idle' }) => paddlePreloader.preload(options);
export const getPaddleInstance = () => paddlePreloader.getInstance();
export const isPaddleReady = () => paddlePreloader.isReady();
export const getPaddleStatus = () => paddlePreloader.getStatus();

export default paddlePreloader;